<template>
  <v-layout>
    <FileUpload
      v-show="false"
      ref="upload"
      :input-id="key"
      class="btn btn-primary"
      :extensions="extensions"
      :accept="accept"
      :multiple="multiple"
      :size="maxFileSize"
      :value="files"
      :post-action="url"
      :thread="thread"
      :custom-action="customAction"
      v-on="inputListeners"
      @input-filter="inputFilter"
      @input-file="inputFile"
    />
    <v-dialog
      v-model="dialog"
      :max-width="700"
      :persistent="true"
      @keydown.esc="close"
      @keydown.enter="chooseFile"
    >
      <v-card>
        <v-app-bar
          dark
          dense
          text
          color="primary"
        >
          <v-toolbar-title class="white--text">
            {{ $t($lang.file.UPLOAD_FILE) }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            text
            icon
            @click.native="close"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-app-bar>
        <v-card-text class="pt-5">
          <v-layout
            v-if="!multiple"
            row
          >
            <v-flex
              v-if="files.length === 0"
              xs12
            >
              <v-alert
                type="info"
                :value="true"
              >
                {{ $t($lang.file.YOU_HAVE_NOT_SELECTED_ANY_FILE_YET) }}
              </v-alert>
            </v-flex>
            <v-flex
              v-else
              xs12
              class="elevation-1"
            >
              <v-layout>
                <v-flex xs8>
                  <v-subheader>{{ files[0].file.name }}</v-subheader>
                </v-flex>
                <v-flex xs2>
                  <v-subheader>
                    <FileUploadStatus
                      :color="getStatusColor(files[0])"
                      :text="getStatusText(files[0])"
                    />
                    %{{ files[0].progress }}
                  </v-subheader>
                </v-flex>
                <v-flex xs2>
                  <v-subheader>
                    <v-btn
                      text
                      icon
                      @click.native="remove(files[0])"
                    >
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-subheader>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-data-table
            v-else
            class="elevation-1"
            :headers="headers"
            :items="files"
          >
            <template
              #items="props"
            >
              <td>{{ props.item.file.name }}</td>
              <td>
                <FileUploadStatus
                  :color="getStatusColor(props.item)"
                  :text="getStatusText(props.item)"
                />
                <template v-if="props.item.progress">
                  %{{ props.item.progress }}
                </template>
              </td>
              <td class="text-xs-center">
                <v-btn
                  v-if="!props.item.active && props.item.error"
                  text
                  icon
                  @click.native="startUploadFile(props.item)"
                >
                  <v-icon>refresh</v-icon>
                </v-btn>
                <v-btn
                  v-if="props.item.active"
                  text
                  icon
                  @click.native="stopUploadFile(props.item)"
                >
                  <v-icon>stop</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  @click.native="remove(props.item)"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn
            color="primary"
            @click.native="chooseFile"
          >
            {{ $t($lang.file.FILE) }}
            {{ multiple ? $t($lang.app.ADD) : $t($lang.app.SELECT) }}
          </v-btn>
          <v-btn
            v-if="$refs.upload && !$refs.upload.active && hasError"
            :disabled="files.length < 1"
            @click.native="startUpload"
          >
            {{ $t($lang.app.RESTART) }}
          </v-btn>
          <v-btn
            v-if="$refs.upload && $refs.upload.active"
            @click.native="stopUpload"
          >
            {{ $t($lang.app.STOP) }}
          </v-btn>
          <v-btn
            v-if="uploaded"
            @click.native="complete"
          >
            {{ $t($lang.app.COMPLETE) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>


// TODO add validation functionality and rules prop (validation prop used by Vuetify.
//  let's use the same terminology even after we stop using Vuetify)
import { mapMutations } from 'vuex';
import FileUpload from 'vue-upload-component';

import store from './store';
import mutationTypes from './store/types/mutationTypes';
import FileUploadStatus from './FileUploadStatus.vue';
import {
  UploadingMethods,
  UploadingTypes,
  DefaultFileExtensions,
  DefaultFileAccepts,
} from './constants';
import { DialogColors } from '../../dialogs/constants';
import { isImage, isVideo } from "@/helpers/misc/file-upload";

export default {
  name: 'PiriFileUpload',
  components: {
    FileUpload,
    FileUploadStatus,
  },
  inheritAttrs: false,
  props: {
    extensions: {
      type: String,
      default: DefaultFileExtensions,
    },
    accept: {
      type: String,
      default: DefaultFileAccepts,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 0,
    },
    onlyWarnWhenOversize: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    immediate: {
      type: Boolean,
      default: true,
    },
    uploadingMethod: {
      type: Number,
      default: UploadingMethods.File + UploadingMethods.Cloud,
    },
    url: {
      type: String,
      default: '',
    },
    uploadAuto: {
      type: Boolean,
      default: true,
    },
    thread: {
      type: Number,
      default: 3,
    },
    standalone: {
      type: Boolean,
      default: false,
    },
    beforeUpload: {
      type: Function,
      default: undefined,
    },
  },
  emits: ['complete'],
  data() {
    return {
      initiated: false,
      dialog: false,
      headers: [
        {
          text: this.$t(this.$lang.file.FILE),
          align: 'left',
          sortable: false,
          value: 'name',
        },
        {
          text: this.$t(this.$lang.status.STATUS),
          align: 'left',
          sortable: false,
          value: 'status',
        },
        {
          text: '',
          sortable: false,
          value: 'action',
        },
      ],
      approvedFiles: [],
    };
  },
  computed: {
    files() {
      return this.$store.state[store.key].files[this.key] || [];
    },
    inputListeners() {
      return {
        ...this.$listeners,
        input: event => {
          this.inputUpdate({
            name: this.key,
            files: event,
          });
        },
      };
    },
    key() {
      return this.$vnode.key;
    },
    uploaded() {
      if (this.files.length === 0) {
        return false;
      }

      let file;

      for (let i = 0; i < this.files.length; i++) {
        file = this.files[i];

        if (file.fileObject) {
          if (!file.error && !file.success) {
            return false;
          }

          if (file.error) {
            return false;
          }
        }
      }

      return true;
    },
    hasError() {
      return this.files.filter(file => file.error).length > 0;
    },
    maxFileSize() {
      return this.onlyWarnWhenOversize ? 0 : this.maxSize;
    },
  },
  mounted() {
    if (!this.initiated && this.standalone) {
      // TODO if needed, check if this is an instance without UI
      this.initiateUploader({
        key: this.$vnode.key,
        instance: this,
      });
      this.initiated = true;
    }
  },
  methods: {
    ...mapMutations({
      initiateUploader: mutationTypes.INITIATE_UPLOADER,
    }),
    addFiles(files) {
      this.$refs.upload.add(files);
    },
    inputUpdate(fileBatch) {
      this.$store.commit(mutationTypes.UPDATE_FILES, fileBatch);
    },
    async confirmWithUserToUploadFileBiggerThanSuggestedMaximumSize(fileSize) {
      let message = this.$t(
        this.$lang.file.ARE_YOU_SURE_YOU_WANT_TO_UPLOAD_FILE_BIGGER_THAN_SUGGESTED_MAX_SIZE,
      );

      const maxFileSizeInMb = this.maxSize / (1024 * 1024);
      const selectedFileSizeInMb = fileSize / (1024 * 1024);

      message = message
        .replace('{0}', maxFileSizeInMb.toFixed(2))
        .replace('{1}', selectedFileSizeInMb.toFixed(2));

      return this.$root.$confirm(this.$t(this.$lang.app.WARNING), message, {
        color: DialogColors.ERROR,
      });
    },
    showFileSizeIsBigAlert() {
      const maxFileSizeInMb = this.maxSize / (1024 * 1024);
      let message = this.$t(this.$lang.file.FILE_SIZE_SHOULD_NOT_BE_BIGGER_THAN_SUGGESTED_MAX_SIZE);

      message = message.replace('{0}', maxFileSizeInMb.toFixed(2));

      this.$root.$alert(this.$t(this.$lang.app.ERROR), message, {
        color: DialogColors.ERROR,
      });
    },
    async inputFilter(newFile, oldFile, prevent) {
      if (!newFile) {
        return undefined;
      }

      // Filter system files or hide files
      if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
        return prevent();
      }

      // Filter php html js file
      if (/\.(html?|jsx?|js?)$/i.test(newFile.name)) {
        return prevent();
      }

      this.$refs.upload.active = false;
      const isFileSizeBiggerThanSuggested = this.maxSize > 0 && newFile.size > this.maxSize;

      if (isFileSizeBiggerThanSuggested && !this.onlyWarnWhenOversize) {
        this.showFileSizeIsBigAlert();
        this.$refs.upload.remove(newFile);

        return prevent();
      }

      const isFileAlreadyApproved = !!this.approvedFiles.find(id => id === newFile.id);
      let isFileApproved = isFileAlreadyApproved
        || (isFileSizeBiggerThanSuggested
          ? await this.confirmWithUserToUploadFileBiggerThanSuggestedMaximumSize(newFile.size)
          : true);

      if (this.beforeUpload && !isFileAlreadyApproved) {
        isFileApproved = await this.beforeUpload(newFile);
      }

      if (isFileApproved) {
        if (!isFileAlreadyApproved) {
          this.approvedFiles.push(newFile.id);
        }

        this.$refs.upload.active = true;
      } else {
        this.$refs.upload.remove(newFile);

        return prevent();
      }

      return undefined;
    },
    inputFile(newFile, oldFile) {
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (this.uploadAuto && !this.$refs.upload.active) {
          this.$refs.upload.active = false;
        }
      }
    },
    async customAction(file, component) {
      if (!this.url) {
        throw new Error('No valid post action found.');
      }

      if ((this.uploadingMethod & UploadingMethods.Any) === 0) {
        throw new Error('No valid uploading method found.');
      }

      file.data.method = this.uploadingMethod;

      if (isImage(file.file.name)) {
        file.data.type = UploadingTypes.Image;
      } else if (isVideo(file.file.name)) {
        file.data.type = UploadingTypes.Video;
      } else {
        throw new Error(this.$t(this.$lang.file.YOU_CAN_ONLY_UPLOAD_VIDEO_OR_IMAGE));
      }

      if (this.$refs.upload.features.html5) {
        return component.uploadHtml5(file);
      }

      return component.uploadHtml4(file);
    },
    async chooseFile() {
      // If multiple is true, that means you can freely add file. So, its value directly becomes the value of confirmed.
      let confirmed = this.multiple;

      if (!this.multiple) {
        if (this.uploaded) {
          confirmed = await this.$root.$confirm(
            this.$t(this.$lang.file.SELECT_FILE),
            this.$t(this.$lang.file.ARE_YOU_SURE_YOU_WANT_TO_CHANGE_FILE),
            {
              color: DialogColors.ERROR,
              buttonColors: {
                accept: DialogColors.DEFAULT,
                decline: DialogColors.ERROR,
              },
            },
          );
        } else {
          confirmed = true;
        }
      }

      if (confirmed) {
        this.$el.querySelector('input[type="file"]').click();
      }
    },
    open() {
      this.dialog = true;
    },
    async close() {
      let confirm;

      if (this.files.length > 0 && !this.uploaded) {
        confirm = this.$root.$confirm(
          this.$t(this.$lang.file.SELECT_FILE),
          this.$t(this.$lang.file.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_UPLOAD),
          {
            color: DialogColors.ERROR,
            buttonColors: {
              accept: DialogColors.DEFAULT,
              decline: DialogColors.ERROR,
            },
          },
        );
      } else if (this.$refs.upload.active) {
        confirm = this.$root.$confirm(
          this.$t(this.$lang.file.UPLOAD_IN_PROGRESS),
          this.$t(this.$lang.file.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_UPLOAD_IN_PROGRESS),
          {
            color: DialogColors.ERROR,
            buttonColors: {
              accept: DialogColors.DEFAULT,
              decline: DialogColors.ERROR,
            },
          },
        );
      } else {
        confirm = Promise.resolve(true);
      }

      const confirmed = await confirm;

      if (confirmed) {
        this.stopUpload();
        this.clear();
        this.dialog = false;
      }
    },
    startUploadFile(file) {
      this.$refs.upload.update(file, {
        active: true,
        error: undefined,
        success: undefined,
      });
    },
    stopUploadFile(file) {
      this.$refs.upload.update(file, {
        active: false,
        error: 'abort',
      });
    },
    startUpload() {
      this.files
        .filter(file => !file.success || file.error)
        .forEach(file => {
          this.$refs.upload.update(file, {
            active: true,
            error: undefined,
            success: undefined,
          });
        });
    },
    stopUpload() {
      this.$refs.upload.active = false;
    },
    clear() {
      this.approvedFiles = [];
      this.$refs.upload.clear();
    },
    async remove(item) {
      const confirmed = await this.$root
        .$confirm(
          this.$t(this.$lang.file.CANCEL_UPLOAD_FILE),
          this.$t(this.$lang.file.ARE_YOU_SURE_YOU_WANT_TO_REMOVE_SELECTED_FILE),
          {
            color: DialogColors.ERROR,
            buttonColors: {
              accept: DialogColors.DEFAULT,
              decline: DialogColors.ERROR,
            },
          },
        );

      if (confirmed) {
        this.approvedFiles.splice(this.approvedFiles.indexOf(item.id), 1);
        this.$refs.upload.remove(item);
      }
    },
    getStatusColor(item) {
      if (item.success) {
        return 'green';
      }

      if (item.error) {
        return 'red darken-1';
      }

      if (!item.active) {
        return 'blue darken-4';
      }

      return 'orange darken-1';
    },
    getStatusText(item) {
      if (item.success) {
        return this.$t(this.$lang.app.SUCCESSFUL);
      }

      if (item.error) {
        return item.error;
      }

      if (!item.active) {
        return this.$t(this.$lang.app.READY);
      }

      return this.$t(this.$lang.file.UPLOADING);
    },
    complete() {
      this.$emit(
        'complete',
        this.files.map(file => ({
          file: file.file,
          data: file.response,
          size: file.size,
          type: file.type,
        })),
      );
      this.close();
    },
  },
};
</script>
